import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { isEmpty } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { Pill } from '@/components/pills/Pill'

import { DEFAULT_LOOKUP_ENTITY_TYPE, DEFAULT_LOOKUP_QUESTION, TOOL_TYPES } from './tools.constants'
import { getFieldTypeLabel, isCardOnlyPrompt } from './tools.helpers'

export const ToolCardContent = ({ tool, fieldTypes, getAgentName, getPlaybookName }) => {
  const [expanded, setExpanded] = useState(false)
  const [overflows, setOverflows] = useState(false)
  const promptRef = useRef(null)
  const isForm = tool.type === TOOL_TYPES.FORM
  const fields = tool.config?.fields || []
  const entityType = tool.config?.entities?.[0]?.type || DEFAULT_LOOKUP_ENTITY_TYPE
  const instructions = isCardOnlyPrompt(tool.prompt?.user_prompt) ? '' : tool.prompt?.user_prompt
  const lookupText = `${tool.config?.lookup_question || DEFAULT_LOOKUP_QUESTION}${
    isEmpty(instructions) ? '' : `\n\n${instructions}`
  }`

  useEffect(() => {
    const element = promptRef.current
    if (!expanded && element) {
      setOverflows(element.scrollHeight > element.clientHeight)
    }
  }, [lookupText, expanded])

  return (
    <>
      {isForm ? (
        <div className="tool-card-fields" data-testid="tool-card-fields">
          {fields.map((field) => (
            <Pill key={field.key} small>
              {field.label}
            </Pill>
          ))}
          {isEmpty(fields) && <p className="tool-card__default-access">No fields yet</p>}
        </div>
      ) : (
        <div className="tool-card-lookup">
          <p className="tool-card__default-access">
            {`Looks up each ${getFieldTypeLabel(fieldTypes, entityType).toLowerCase()}`}
          </p>
          <div
            ref={promptRef}
            className={classNames('tool-card__prompt', { 'tool-card__prompt--expanded': expanded })}
          >
            {lookupText}
          </div>
          {(overflows || expanded) && (
            <Button text className="tool-card__show-more" onClick={() => setExpanded(!expanded)}>
              {expanded ? 'Show Less' : 'Show More'}
            </Button>
          )}
        </div>
      )}
      {isEmpty(tool.user_whitelist) &&
      isEmpty(tool.user_blacklist) &&
      isEmpty(tool.playbook_whitelist) &&
      isEmpty(tool.playbook_blacklist) ? (
        <p className="tool-card__default-access">Available to everyone on all playbooks</p>
      ) : (
        <div className="tool-card__filters">
          {!isEmpty(tool.user_whitelist) && (
            <div className="tool-card__filter-group">
              <span className="tool-card__filter-label">Agents allowed</span>
              <div className="tool-card__pills">
                {tool.user_whitelist.map((id) => (
                  <Pill key={id} small success>
                    {getAgentName(id)}
                  </Pill>
                ))}
              </div>
            </div>
          )}
          {!isEmpty(tool.user_blacklist) && (
            <div className="tool-card__filter-group">
              <span className="tool-card__filter-label">Agents blocked</span>
              <div className="tool-card__pills">
                {tool.user_blacklist.map((id) => (
                  <Pill key={id} small critical>
                    {getAgentName(id)}
                  </Pill>
                ))}
              </div>
            </div>
          )}
          {!isEmpty(tool.playbook_whitelist) && (
            <div className="tool-card__filter-group">
              <span className="tool-card__filter-label">Playbooks allowed</span>
              <div className="tool-card__pills">
                {tool.playbook_whitelist.map((cid) => (
                  <Pill key={cid} small success>
                    {getPlaybookName(cid)}
                  </Pill>
                ))}
              </div>
            </div>
          )}
          {!isEmpty(tool.playbook_blacklist) && (
            <div className="tool-card__filter-group">
              <span className="tool-card__filter-label">Playbooks blocked</span>
              <div className="tool-card__pills">
                {tool.playbook_blacklist.map((cid) => (
                  <Pill key={cid} small critical>
                    {getPlaybookName(cid)}
                  </Pill>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  )
}
